import React, { useEffect, useRef } from "react";
import { submitGamesMeasurement, type XOGamesMeasurementEvent } from "../lib/xoInput";

const proof = {
  artifact: { title: "Grand Theft Auto V", platform: "Xbox 360", artifact_ref: "game-gta-v-x360-m1-001" },
  verification: { functional_status: "UNVERIFIED" },
  rights: { scope: "physical_copy_only" },
};

const SOURCE_CLASSES = new Set<XOGamesMeasurementEvent["source_class"]>([
  "community",
  "creator",
  "press",
  "grader",
  "publisher",
  "other_organic",
  "unknown",
]);

function proofArrivalFromLocation(): XOGamesMeasurementEvent | null {
  if (typeof window === "undefined") return null;
  const query = new URLSearchParams(window.location.search);
  const rawSource = query.get("xo_source_class") ?? "unknown";
  if (!SOURCE_CLASSES.has(rawSource as XOGamesMeasurementEvent["source_class"])) return null;
  const paidRaw = query.get("xo_paid");
  if (paidRaw !== null && paidRaw !== "0" && paidRaw !== "1") return null;
  const sourceRef = query.get("xo_source_ref");
  if (sourceRef !== null && !sourceRef.trim()) return null;
  return {
    event_name: "proof_referral_arrival",
    proof_ref: proof.artifact.artifact_ref,
    source_class: rawSource as XOGamesMeasurementEvent["source_class"],
    source_ref: sourceRef?.trim() || null,
    is_paid: paidRaw === "1",
  };
}

export default function XOGamesSecondLifeProofPage() {
  const eventId = useRef<string | null>(null);

  useEffect(() => {
    const event = proofArrivalFromLocation();
    if (!event) return;
    eventId.current ??= globalThis.crypto?.randomUUID?.() ?? `proof-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    void submitGamesMeasurement(event, { idempotencyKey: eventId.current }).catch(() => undefined);
  }, []);

  return (
    <main data-testid="xo-games-second-life-proof" style={{ width: "min(820px, calc(100% - 2rem))", margin: "0 auto", padding: "clamp(2.5rem, 7vw, 6rem) 0", color: "#F6F0E6" }}>
      <header>
        <div style={{ fontSize: "0.78rem", letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.62 }}>xo.games · Second Life Proof</div>
        <h1 style={{ fontSize: "clamp(2.5rem, 7vw, 5rem)", lineHeight: 1, marginBottom: "0.8rem" }}>This copy continued.</h1>
        <p style={{ lineHeight: 1.65, opacity: 0.78 }}>A privacy-safe proof format for a physical game that passed to its next Keeper while its earlier continuity remained intact.</p>
      </header>

      <section style={{ marginTop: "2rem", border: "1px solid rgba(246,240,230,0.16)", borderRadius: 18, padding: "1.4rem" }}>
        <div style={{ opacity: 0.6 }}>{proof.artifact.platform}</div>
        <h2>{proof.artifact.title}</h2>
        <code style={{ opacity: 0.6 }}>{proof.artifact.artifact_ref}</code>
        <dl style={{ display: "grid", gap: "0.8rem", marginTop: "1.5rem" }}>
          <div><dt>Functional status</dt><dd>{proof.verification.functional_status}</dd></div>
          <div><dt>Rights scope</dt><dd>{proof.rights.scope}</dd></div>
          <div><dt>Lifecycle proof</dt><dd>Completed-transfer proof is published only from an accepted Passage + receiving-Keeper receipt.</dd></div>
        </dl>
      </section>

      <section style={{ marginTop: "1rem", opacity: 0.72, lineHeight: 1.65 }}>
        <strong>Proof boundary</strong>
        <p>This share surface exposes evidence-backed public facts only. It does not establish payment, legal title, grading, game functionality, publisher/platform rights, digital entitlement, or a live marketplace.</p>
      </section>
      <a href="/games">Return to xo.games</a>
    </main>
  );
}
